import {
  Dispatch,
  memo,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useNavigate, useSearchParams } from 'react-router'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { useQueryClient } from '@tanstack/react-query'
import { DocumentIdOverlay } from '@/hooks/useDocumentIdOverlay'
import useGetDocumentWorkflowAndState from '@/hooks/useGetDocumentWorkflowAndState'
import queryKeys from '@/service-library/query-keys'
import useGetValidationDocDataQueries from '@/services/hooks/useGetValidationDocDataQueries'
import { DocumentView } from '@/types/workflows'
import {
  DOCUMENT_VIEW_PARAM,
  parseDocumentViewParam,
} from '@/utils/document-overlay'
import { Dialog } from '@/components/dialog'
import DrawerPortalProvider from '@/components/drawer/DrawerPortalProvider'
import DocumentCurrentStateViewProvider, {
  useDocumentCurrentStateViewContext,
} from '@/components/image-zoom-pan/providers/DocumentCurrentStateViewProvider'
import DocumentProvider from '@/components/image-zoom-pan/providers/DocumentProvider'
import { useNotifications } from '@/components/notifications/NotificationProvider'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import QueryKeysMapProvider from '@/components/providers/QueryKeyProvider'
import DisabledDocumentsProvider from '@/components/validation/providers/DisabledDocumentsProvider'
import DocumentChipsProvider from '@/components/validation/providers/DocumentChipsProvider'
import DocumentRowsProvider from '@/components/validation/providers/DocumentRowsProvider'
import DocumentRowValuesProvider from '@/components/validation/providers/DocumentRowValuesProvider'
import DocumentsChangeSetsProvider from '@/components/validation/providers/DocumentsChangeSetsProvider'
import ExcludedFieldsForFavoritesProvider from '@/components/validation/providers/ExcludedFieldsForFavoritesProvider'
import FieldErrorProvider from '@/components/validation/providers/FieldErrorProvider'
import SelectedFieldProvider from '@/components/validation/providers/SelectedFieldProvider'
import { useSelectedWorkflowStateContext } from '@/components/workflows/SelectedWorkflowStateProvider'
import ValidationOverlayContent from './ValidationOverlayContent'

const hotKeyConfig = {
  preventDefault: true,
  enableOnFormTags: true,
}

type CommonProps = {
  hasPreviousDocument: boolean
  hasNextDocument: boolean
  selectedIndex: number
  goToPreviousDocument: () => void
  goToNextAvailableDocumentOrClose: () => void
  refetchTableData: () => void
  /** Only needed where the overlay was opened from a table with row selection. */
  setUseOnlySelected?: Dispatch<SetStateAction<boolean>>
  documentsCount?: number
}

type ValidationOverlayProps = CommonProps & {
  documentOverlay: DocumentIdOverlay
  nextDocumentId?: string
  /**
   * Stay on the current page when the document's workflow state isn't the one
   * selected in the URL. By default the overlay jumps to the Documents tab for
   * that state.
   */
  disableStateRedirect?: boolean
  /**
   * Open on this view instead of the workflow state's default. Falls back to
   * the `document_view` URL param, which links use to open a document on a
   * specific view.
   */
  preferredView?: DocumentView
}

export type ValidationOverlayContentContainerProps = CommonProps & {
  dataQueries: ReturnType<typeof useGetValidationDocDataQueries>
  documentOverlay: DocumentIdOverlay
  documentIsInFlight: boolean
  documentId?: string
}

function ValidationOverlayContentContainer({
  documentId,
  ...props
}: ValidationOverlayContentContainerProps) {
  const {
    dataQueries,
    documentOverlay: { overlay },
    documentIsInFlight,
    hasNextDocument,
    goToPreviousDocument,
    goToNextAvailableDocumentOrClose,
  } = props
  const { documentCurrentStateView } = useDocumentCurrentStateViewContext()

  useHotkeys(
    'alt+1',
    () => {
      if (!documentIsInFlight) {
        goToPreviousDocument()
      }
    },
    {
      ...hotKeyConfig,
      enabled: overlay.isOpen,
    },
    [documentIsInFlight, goToPreviousDocument],
  )

  useHotkeys(
    'alt+2',
    () => {
      if (!documentIsInFlight && hasNextDocument) {
        goToNextAvailableDocumentOrClose()
      }
    },
    {
      ...hotKeyConfig,
      enabled: overlay.isOpen,
    },
    [documentIsInFlight, hasNextDocument, goToNextAvailableDocumentOrClose],
  )

  return (
    <DocumentProvider
      query={dataQueries.documentQuery}
      isDisabled={
        documentCurrentStateView === 'comparison' ||
        documentCurrentStateView === 'approvals' ||
        documentIsInFlight
      }
    >
      <DocumentRowsProvider query={dataQueries.documentRowsQuery}>
        <DocumentRowValuesProvider query={dataQueries.documentRowValuesQuery}>
          <FieldErrorProvider>
            <DocumentChipsProvider
              documentId={documentId}
              query={dataQueries.documentChipsQuery}
            >
              {/* This helps preventing the field panel from showing up outside the overlay.*/}
              <DrawerPortalProvider>
                <SelectedFieldProvider>
                  <ExcludedFieldsForFavoritesProvider
                    isDisabled={documentCurrentStateView !== 'validation'}
                  >
                    <ValidationOverlayContent
                      documentView={documentCurrentStateView}
                      {...props}
                    />
                  </ExcludedFieldsForFavoritesProvider>
                </SelectedFieldProvider>
              </DrawerPortalProvider>
            </DocumentChipsProvider>
          </FieldErrorProvider>
        </DocumentRowValuesProvider>
      </DocumentRowsProvider>
    </DocumentProvider>
  )
}

const ValidationOverlay = ({
  documentOverlay,
  refetchTableData,
  setUseOnlySelected,
  nextDocumentId,
  disableStateRedirect = false,
  preferredView,
  ...props
}: ValidationOverlayProps) => {
  const { project } = useProjectContext()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const viewFromUrl = parseDocumentViewParam(
    searchParams.get(DOCUMENT_VIEW_PARAM),
  )

  const { selectedWorkflowState } = useSelectedWorkflowStateContext()

  const { overlay, documentIdState, closeDocument } = documentOverlay
  const documentId = documentIdState.value

  const dataQueries = useGetValidationDocDataQueries({
    documentId,
  })

  const document = dataQueries.documentQuery.document

  const { project_id, workflow_states_ids } = document || {}

  // Completing a document moves it to another workflow state. A closed overlay
  // must not follow it there, or the user is pulled out of the table they are in.
  const needToUpdateUrl = useMemo(
    () =>
      overlay.isOpen &&
      !disableStateRedirect &&
      !!document?.id &&
      (project_id !== project.id ||
        !workflow_states_ids?.includes(selectedWorkflowState?.id || '')),
    [
      overlay.isOpen,
      disableStateRedirect,
      document?.id,
      project_id,
      project.id,
      workflow_states_ids,
      selectedWorkflowState?.id,
    ],
  )

  const { workflowId, workflowStateId } = useGetDocumentWorkflowAndState({
    document,
    enabled: needToUpdateUrl,
    useCurrentProject: project_id === project.id,
  })

  const documentIsInFlight =
    document && selectedWorkflowState
      ? !!document.document_workflow_states?.find(
          ({ workflow_state_id }) =>
            workflow_state_id === selectedWorkflowState.id,
        )?.processing_started_at
      : true

  // This pre-fetches the next document and puts it into cache so it'll load immediately when the user switches to it
  const { documentQuery: nextDocumentQuery } = useGetValidationDocDataQueries({
    documentId: nextDocumentId || '',
    enabled: !documentIsInFlight && !!nextDocumentId,
  })

  const { document: nextDocument } = nextDocumentQuery

  const queryClient = useQueryClient()
  useNotifications({
    keys: [
      'pdWorkflow/events.document_active',
      'pdWorkflow/events.event_complete',
      'pdWorkflow/events.event_failed',
      'pdWorkflow/events.event_cancelled',
    ],
    callback: ({ updated_entity_ids }) => {
      if (documentId && updated_entity_ids.includes(documentId)) {
        queryClient.invalidateQueries(
          { queryKey: queryKeys.documents.all },
          { cancelRefetch: false },
        )
        queryClient.invalidateQueries(
          { queryKey: queryKeys.documentRows.all },
          { cancelRefetch: false },
        )
        queryClient.invalidateQueries(
          { queryKey: queryKeys.documentRowValues.all },
          { cancelRefetch: false },
        )
        queryClient.invalidateQueries(
          { queryKey: queryKeys.documentChips.all },
          { cancelRefetch: false },
        )
      }
    },
  })

  // This pre-fetches the next document's image so it will load from cache
  useEffect(() => {
    if (!nextDocument || !overlay.isOpen) return
    const img = new Image()
    img.src = nextDocument?.document_pages?.[0]?.image_urls.full
  }, [nextDocument, overlay.isOpen])

  // The view param only applies to the document the link opened, which is the
  // one in the URL when the page loads. Once another document is showing
  // (Next/Previous) or none is (closed), ignore it and drop it from the URL,
  // so every other document opens on its state's default view.
  const [viewDocumentId] = useState(() =>
    viewFromUrl ? documentId : undefined,
  )
  const viewForDocument =
    viewFromUrl && documentId === viewDocumentId ? viewFromUrl : undefined
  useEffect(() => {
    if (!viewFromUrl || documentId === viewDocumentId) return
    setSearchParams(
      (params) => {
        params.delete(DOCUMENT_VIEW_PARAM)
        return params
      },
      { replace: true },
    )
  }, [documentId, viewDocumentId, viewFromUrl, setSearchParams])

  // If the project ID or the workflow state of document doesn't match the ones in the url, update the url.
  useEffect(() => {
    if (needToUpdateUrl && workflowId && workflowStateId) {
      // Keep the requested view, so a link that had to be redirected still
      // opens on it.
      const viewParam = viewForDocument
        ? `&${DOCUMENT_VIEW_PARAM}=${viewForDocument}`
        : ''
      navigate(
        `/projects/${project_id}/documents?workflow=${workflowId}&workflow_state=${workflowStateId}&document_id=${documentId}${viewParam}`,
        {
          replace: true,
        },
      )
    }
  }, [
    documentId,
    navigate,
    needToUpdateUrl,
    project.id,
    project_id,
    document,
    selectedWorkflowState?.id,
    workflowId,
    workflowStateId,
    workflow_states_ids,
    viewForDocument,
  ])

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <QueryKeysMapProvider>
        <DocumentsChangeSetsProvider>
          <DisabledDocumentsProvider>
            <DocumentCurrentStateViewProvider
              document={document}
              preferredView={preferredView ?? viewForDocument}
            >
              <Dialog
                {...overlay}
                fullScreen
                onClose={() => {
                  closeDocument()
                  refetchTableData()
                  setUseOnlySelected?.(false)
                }}
              >
                <ValidationOverlayContentContainer
                  documentId={documentId}
                  documentOverlay={documentOverlay}
                  dataQueries={dataQueries}
                  documentIsInFlight={documentIsInFlight}
                  refetchTableData={refetchTableData}
                  setUseOnlySelected={setUseOnlySelected}
                  {...props}
                />
              </Dialog>
            </DocumentCurrentStateViewProvider>
          </DisabledDocumentsProvider>
        </DocumentsChangeSetsProvider>
      </QueryKeysMapProvider>
    </LocalizationProvider>
  )
}

export default memo(ValidationOverlay)
